import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { api, mediaUrl } from "@/lib/api";
import { ResourceGrid } from "@/components/ResourceCard";

export default function Library() {
  const { settings } = useSite();
  const [params, setParams] = useSearchParams();
  const area = params.get("area") || "";
  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const freeOnly = params.get("free") === "1";
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [search, setSearch] = useState(q);
  const current = settings.library_areas.find((a) => a.slug === area);

  const update = (patch) => {
    const next = { area, category, q, free: freeOnly ? "1" : "", ...patch };
    setParams(Object.fromEntries(Object.entries(next).filter(([, v]) => v)));
  };

  useEffect(() => {
    api.get("/resources", { params: { area: area || undefined, category: category || undefined, q: q || undefined, access: freeOnly ? "free" : undefined } }).then((r) => setItems(r.data));
  }, [area, category, q, freeOnly]);

  useEffect(() => {
    if (!area) return setCats([]);
    Promise.all([api.get("/categories", { params: { area } }), api.get("/resources", { params: { area } })]).then(([c, r]) => {
      const used = new Set(r.data.map((x) => x.category));
      setCats(c.data.filter((x) => used.has(x.name)).map((x) => x.name));
    });
  }, [area]);

  const browsing = area || q || category;

  return (
    <div className="container-bt py-14 sm:py-20" data-testid="library-page">
      <div className="mb-10 max-w-3xl">
        <p className="eyebrow mb-4">Growth Library</p>
        <h1 className="text-4xl leading-tight sm:text-5xl lg:text-6xl" data-testid="library-title">{current ? current.title : "Find what you need today."}</h1>
        {current && <p className="mt-4 text-base md:text-lg">{current.description}</p>}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); update({ q: search }); }} className="relative mb-6 max-w-xl">
        <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-bt-sagedeep" size={20} />
        <input className="input-bt !pl-14" placeholder="Search affirmations, prompts, tools..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="library-search-input" />
        {q && <button type="button" onClick={() => { setSearch(""); update({ q: "" }); }} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full p-1 hover:bg-bt-sage" aria-label="Clear search" data-testid="library-search-clear"><X size={18} /></button>}
      </form>

      <div className="-mx-5 mb-4 flex gap-2 overflow-x-auto px-5 pb-2" data-testid="library-area-tabs">
        <button className={`chip shrink-0 ${!area ? "chip-active" : ""}`} onClick={() => update({ area: "", category: "" })} data-testid="library-area-all">Everything</button>
        {settings.library_areas.map((a) => (
          <button key={a.slug} className={`chip shrink-0 ${area === a.slug ? "chip-active" : ""}`} onClick={() => update({ area: a.slug, category: "" })} data-testid={`library-area-${a.slug}`}>{a.title}</button>
        ))}
      </div>

      {cats.length > 1 && (
        <div className="-mx-5 mb-4 flex gap-2 overflow-x-auto px-5 pb-2" data-testid="library-category-filters">
          {cats.map((c) => (
            <button key={c} onClick={() => update({ category: category === c ? "" : c })} data-testid={`library-category-${c.toLowerCase().replace(/\W+/g, "-")}`}
              className={`shrink-0 rounded-full px-4 py-2 text-sm transition-colors ${category === c ? "bg-bt-sage font-semibold text-bt-primary" : "text-bt-text hover:bg-bt-sage"}`}>{c}</button>
          ))}
        </div>
      )}
      <label className="mb-10 inline-flex cursor-pointer items-center gap-3 text-sm font-medium text-bt-heading">
        <input type="checkbox" className="h-5 w-5 accent-[color:var(--bt-primary)]" checked={freeOnly} onChange={(e) => update({ free: e.target.checked ? "1" : "" })} data-testid="library-free-only-toggle" />
        Free only
      </label>

      {!browsing && (
        <div className="mb-16 grid gap-4 sm:grid-cols-2" data-testid="library-area-cards">
          {settings.library_areas.map((a) => (
            <button key={a.slug} onClick={() => update({ area: a.slug })} data-testid={`library-area-card-${a.slug}`}
              className="group flex items-center gap-5 rounded-[28px] border border-bt-border bg-bt-surface p-4 text-left transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-bt-primary">
              {a.image && <img src={mediaUrl(a.image)} alt="" className="h-24 w-24 shrink-0 rounded-[22px] object-cover" />}
              <div>
                <h3 className="text-2xl">{a.title}</h3>
                <p className="mt-1 text-sm text-bt-text">{a.description}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {browsing && <p className="mb-6 text-sm text-bt-sagedeep" data-testid="library-result-count">{items.length} resource{items.length === 1 ? "" : "s"}</p>}
      {!browsing && <h2 className="mb-8 text-3xl">Newest</h2>}
      {items.length ? <ResourceGrid items={browsing ? items : items.slice(0, 6)} testid="library-grid" /> : (
        <div className="rounded-[28px] bg-bt-sage p-10 text-center" data-testid="library-empty">
          <p className="font-heading text-2xl text-bt-heading">Nothing here yet.</p>
          <p className="mt-2">Try another search or path.</p>
        </div>
      )}
    </div>
  );
}
